import { client } from '@api/rest/client';
import MaterialIcon from '@components/common/MaterialIcon';
import ButtonT from '@components/tailwind-components/Button/ButtonT';
import InputT from '@components/tailwind-components/Input/InputT';
import LabelT from '@components/tailwind-components/Label/LabelT';
import type { WebclientAccessUsageResponseSchemaDTO } from '@conversed/rest/src/contracts/webclient-access/webclient-access.dto';
import { formatShortDate } from '@helpers/dateHelpers';
import { useModal } from '@hooks/modal/useModal';
import { useMainSelector } from '@hooks/useMainSelector';
import type React from 'react';
import type { ChangeEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWebclient } from '../../../../hooks/settings/webclients/useWebclient';
import { useWebclientActions } from '../../../../hooks/settings/webclients/useWebclientActions';

type TWebclientAccessUsageEntry =
  WebclientAccessUsageResponseSchemaDTO['entries'][number];

const CONFIRMATION_INPUT_ID = 'delete-webclient-config-name';

const RECENT_USAGE_DAYS = 7;
const RECENT_USAGE_WINDOW_MS = RECENT_USAGE_DAYS * 24 * 60 * 60 * 1000;

export const DeleteWebclientConfigModal: React.FC = () => {
  const { selectedWebclientConfig } = useWebclient();
  const { deleteWebclientConfig } = useWebclientActions();
  const { hideModal } = useModal();
  const {
    settings: {
      webclient: { isEnabledWebclientAccess },
    },
  } = useMainSelector();
  const { t } = useTranslation();
  const [confirmationText, setConfirmationText] = useState('');
  const [usage, setUsage] = useState<WebclientAccessUsageResponseSchemaDTO>();
  const [isLoadingUsage, setIsLoadingUsage] = useState(true);

  const configIdentifier = selectedWebclientConfig?.identifier;
  const configName = selectedWebclientConfig?.name ?? '';

  useEffect(() => {
    if (!configIdentifier) {
      return;
    }

    const fetchUsage = async () => {
      setIsLoadingUsage(true);

      try {
        const { data } = await client.webclientAccess.getWebclientAccessUsage({
          params: { configId: configIdentifier },
        });
        setUsage(data);
      } catch (error) {
        console.error(error);
        setUsage(undefined);
      } finally {
        setIsLoadingUsage(false);
      }
    };

    void fetchUsage();
  }, [configIdentifier]);

  const isConfirmed = useMemo(() => {
    if (!configName) {
      return false;
    }

    return confirmationText.trim() === configName;
  }, [confirmationText, configName]);

  // * Only recorded traffic justifies the strong warning. Every case where use
  // * cannot be ruled out counts as in use, because an unread record is not
  // * evidence of an unused config.
  const isKnownToBeInUse = useMemo(() => {
    if (!isEnabledWebclientAccess || isLoadingUsage || !usage) {
      return true;
    }

    const [mostRecentEntry] = usage.entries;

    if (!mostRecentEntry) {
      return false;
    }

    const lastAccessedTime = new Date(mostRecentEntry.lastAccessedAt).getTime();

    return lastAccessedTime >= Date.now() - RECENT_USAGE_WINDOW_MS;
  }, [isEnabledWebclientAccess, isLoadingUsage, usage]);

  // * An unreadable usage list is the reason the warning stays loud, so it
  // * travels inside the banner rather than in a section of its own.
  const usageUnknownReasonKey = useMemo(() => {
    if (isLoadingUsage) {
      return 'settings.webclient.config.delete.usage.loading';
    }

    if (!usage) {
      return 'settings.webclient.config.delete.usage.unavailable';
    }

    if (!isEnabledWebclientAccess) {
      return 'settings.webclient.config.delete.usage.tracking-off';
    }

    return undefined;
  }, [isEnabledWebclientAccess, isLoadingUsage, usage]);

  const handleDelete = () => {
    if (!isConfirmed || !configIdentifier) {
      return;
    }

    void deleteWebclientConfig(configIdentifier);
    hideModal();
  };

  const renderUsageUnknownReason = () => {
    if (!usageUnknownReasonKey) {
      return null;
    }

    return (
      <p className='text-pico text-gray-500'>{t(usageUnknownReasonKey)}</p>
    );
  };

  const renderDeletionWarning = () => {
    if (isKnownToBeInUse) {
      return (
        <div className='flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl'>
          <MaterialIcon
            icon='warning'
            classes={['text-amber-500', 'text-base', 'flex-shrink-0']}
          />
          <div className='flex flex-1 flex-col gap-1'>
            <p className='text-tiny text-gray-700'>
              {t('settings.webclient.config.delete.warning.in-use')}
            </p>
            {renderUsageUnknownReason()}
          </div>
        </div>
      );
    }

    return (
      <div className='flex items-center gap-3 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl'>
        <MaterialIcon
          icon='info'
          classes={['text-gray-400', 'text-base', 'flex-shrink-0']}
        />
        <p className='text-tiny text-gray-700 flex-1'>
          {t('settings.webclient.config.delete.warning.generic')}
        </p>
      </div>
    );
  };

  const renderOriginLabel = (entry: TWebclientAccessUsageEntry) => {
    // * Rows recorded before the pattern normalisation carry no real address,
    // * so there is nothing to link to.
    if (!entry.lastSeenUrl) {
      return <span className='text-xs text-gray-600'>{entry.origin}</span>;
    }

    return (
      <a
        className='text-xs text-primary-base hover:text-primary-dark'
        href={entry.lastSeenUrl}
        target='_blank'
        rel='noreferrer'>
        {entry.origin}
      </a>
    );
  };

  // * Nothing to list means the banner already carries the whole message, so
  // * the section stays out rather than showing a heading over one line.
  const renderUsage = () => {
    if (!usage || usage.entries.length === 0) {
      return null;
    }

    return (
      <div className='flex flex-col gap-2'>
        <LabelT
          text={t('settings.webclient.config.delete.usage.title')}
          subText={t('settings.webclient.config.delete.usage.page-count', {
            count: usage.totalOrigins,
          })}
          subTextClassName='text-pico'
          className='font-medium'
        />
        <ul className='flex flex-col gap-2'>
          {usage.entries.map((entry) => (
            <li key={entry.origin} className='flex flex-col break-all'>
              {renderOriginLabel(entry)}
              <span className='text-pico text-gray-500'>
                {t('settings.webclient.config.delete.usage.entry-summary', {
                  count: entry.accessCount,
                  date: formatShortDate(entry.lastAccessedAt),
                })}
              </span>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  if (!selectedWebclientConfig) {
    return null;
  }

  return (
    <div className='tailwind-modal-form'>
      <div className='tailwind-modal-body flex flex-col gap-4'>
        {renderDeletionWarning()}
        {renderUsage()}
        <div className='tailwind-divider-x' />
        <div className='flex flex-col'>
          <LabelT
            htmlFor={CONFIRMATION_INPUT_ID}
            text={t('settings.webclient.config.delete.name-label', {
              name: configName,
            })}
          />
          <InputT
            id={CONFIRMATION_INPUT_ID}
            type='text'
            placeholder={t('settings.webclient.config.delete.name-placeholder')}
            options={{
              value: confirmationText,
              onChange: (changeEvent: ChangeEvent<HTMLInputElement>) =>
                setConfirmationText(changeEvent.target.value),
            }}
          />
        </div>
      </div>
      <div className='tailwind-modal-footer'>
        <ButtonT
          type='button'
          styling='cancel'
          size='xs'
          rounding='md'
          onClick={() => hideModal()}>
          {t('general.buttons.cancel')}
        </ButtonT>
        <ButtonT
          type='button'
          styling='danger'
          size='xs'
          rounding='md'
          disabled={!isConfirmed}
          onClick={() => handleDelete()}>
          {t('general.buttons.delete')}
        </ButtonT>
      </div>
    </div>
  );
};

export default DeleteWebclientConfigModal;
